import { useCallback, useState } from 'react';

import {
  PageLimit,
  PageLimitMetric,
  PageLimitModal,
} from '../modals/PageLimitModal';
import { useCanPerformOperation } from './useCanPerformOperation';

export function usePageLimitModal() {
  const [pageLimit, setPageLimit] = useState<PageLimit | null>(null);
  const [open, setOpen] = useState(false);

  const showPageLimit = useCallback((limit: PageLimit) => {
    setPageLimit(limit);
    setOpen(true);
  }, []);

  const pageLimitModal = pageLimit && (
    <PageLimitModal
      open={open}
      onClose={() => setOpen(false)}
      pageLimit={pageLimit}
    />
  );

  return { showPageLimit, pageLimitModal };
}

/**
 * Blocks an action that would add `increment` units to a page metric past the
 * plan's allowance, and renders the matching limit dialog. Usage still loading
 * lets the action through; the server enforces the same limit.
 */
export function usePageLimitGate(metric: PageLimitMetric, increment: number) {
  const check = useCanPerformOperation(metric, increment);
  const { showPageLimit, pageLimitModal } = usePageLimitModal();

  const isWithinLimit = () => {
    if (increment === 0 || !check || check.allowed) return true;

    showPageLimit({ metric, current: check.current, limit: check.limit ?? 0 });
    return false;
  };

  return { isWithinLimit, limitModal: pageLimitModal };
}
